import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Stack, IconButton, Tabs, Tab } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OutsideClickHandler from "react-outside-click-handler";
import ProfileMenu from "../../components/ProfileMenu";
import { Images } from "../../assets/common";
import ApiService from "../../services/api-service";
import { HMS } from "../../constants/function";
import { useDispatch } from "react-redux";
import { addSongsAction } from "../../action/songs";
import { useTranslation } from "react-i18next";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import { useHistory } from "react-router-dom";
import FavoriteButton from "../../common/FavoriteButton";
import { toast } from 'react-toastify';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { API } from "../../constants/api"; // Add this import
import VideoApiService from "../../services/video-api-service";

export default function MyFavoritesTab({ userId }) {
  const [tab, setTab] = useState(0); // 0 = Music, 1 = Videos
  const [favorites, setFavorites] = useState([]);
  const [videoFavorites, setVideoFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [loadingVideoFavorites, setLoadingVideoFavorites] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [showStudioPopup, setShowStudioPopup] = useState(false);
  const [studioMusicId, setStudioMusicId] = useState(null);
  const [userSaves, setUserSaves] = useState([]);
  const [userVideoSaves, setUserVideoSaves] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    fetchFavorites();
    fetchVideoFavorites();
  }, [userId]);

 const fetchFavorites = async () => {
  setLoadingFavorites(true);
  try {
    const res = await ApiService.GET_SAVES('music');
    console.log('Favorites API response:', res);
    
    // res.data contains array of song IDs
    const songIds = res.data || [];
    console.log('Favorite song IDs:', songIds);
    
    if (songIds.length === 0) {
      setFavorites([]);
      setUserSaves([]);
      return;
    }
    
    // Fetch full song details for each ID
    const songPromises = songIds.map(async (songId) => {
      try {
        const songDetails = await ApiService.MUSIC_DETAIL(songId);
        return songDetails.result || songDetails.data || songDetails;
      } catch (error) {
        console.error(`Failed to fetch details for song ${songId}:`, error);
        return null;
      }
    });
    
    const songs = await Promise.all(songPromises);
    const validSongs = songs.filter(song => song !== null);
    
    console.log('Fetched song details:', validSongs);
    
    setFavorites(validSongs);
    setUserSaves(songIds); // Use the original IDs array
  } catch (error) {
    console.error('Error fetching favorites:', error);
    toast.error('Failed to load favorites');
  } finally {
    setLoadingFavorites(false);
  }
};

const fetchVideoFavorites = async () => {
  setLoadingVideoFavorites(true);
  try {
    const res = await ApiService.GET_SAVES('videos');
    console.log('Video Favorites API response:', res);
    
    // res.data contains array of video IDs
    const videoIds = res.data || [];
    console.log('Favorite video IDs:', videoIds);
    
    if (videoIds.length === 0) {
      setVideoFavorites([]);
      setUserVideoSaves([]);
      return;
    }
    
    // Fetch full video details for each ID
    const videoPromises = videoIds.map(async (videoId) => {
      try {
        const videoDetails = await VideoApiService.getVideo(videoId);
        const video = videoDetails.data || videoDetails;
        // Add savedAt timestamp for display
        return {
          ...video,
          savedAt: new Date().toISOString()
        };
      } catch (error) {
        console.error(`Failed to fetch details for video ${videoId}:`, error);
        // Return a minimal object if fetch fails
        return {
          _id: videoId,
          title: `Video ${videoId.slice(-8)}`,
          views: 0,
          duration: 0,
          thumbnail: { url: '/placeholder-video.jpg' },
          uploader: { displayArtistName: 'Unknown Creator' },
          savedAt: new Date().toISOString()
        };
      }
    });
    
    const videos = await Promise.all(videoPromises);
    console.log('Fetched video details:', videos);
    
    setVideoFavorites(videos);
    setUserVideoSaves(videoIds);
  } catch (error) {
    console.error('Error fetching video favorites:', error);
    toast.error('Failed to load video favorites');
  } finally {
    setLoadingVideoFavorites(false);
  }
};

// Menu actions for each row
  const getMenuActions = (m) => [
    { key: "info", action: "View Song Information" },
    { key: "add_to_studio", action: "Add to Studio" },
    { key: "remove_favorite", action: "Remove from Favorites" }
  ];

  const getVideoMenuActions = (v) => [
    { key: "watch", action: "Watch Video" },
    { key: "remove_favorite", action: "Remove from Favorites" }
  ];

  const handleMenuAction = async (key, musicId, idx) => {
    setOpen(false);
    setSelectedIndex(null);

    if (key === "info") {
      const nftId = favorites[idx]?.nftId;
      if (nftId) {
        history.push(`/app/NftDetail/${nftId}`);
      } else {
        history.push(`/app/SongDetail/${musicId}`);
      }
    } else if (key === "add_to_studio") {
      setStudioMusicId(musicId);
      setShowStudioPopup(true);
    } else if (key === "remove_favorite") {
      try {
        await ApiService.REMOVE_SAVE('music', { id: musicId });
        setFavorites(favorites.filter(f => f._id !== musicId));
        setUserSaves(userSaves.filter(id => id !== musicId));
        toast.success('Removed from favorites');
      } catch (error) {
        toast.error('Failed to remove from favorites');
      }
    }
  };

  const handleVideoMenuAction = async (key, videoId, idx) => {
    setOpen(false);
    setSelectedIndex(null);

    if (key === "watch") {
      // For favorited videos, we'll navigate to a general video route since we don't have playlist info
      // The video player can handle finding the right playlist when it loads
      history.push(`/app/videos/${videoId}`);
    } else if (key === "remove_favorite") {
      try {
        await ApiService.REMOVE_SAVE('videos', { id: videoId });
        setVideoFavorites(videoFavorites.filter(v => v._id !== videoId));
        setUserVideoSaves(userVideoSaves.filter(id => id !== videoId));
        toast.success('Removed from video favorites');
      } catch (error) {
        toast.error('Failed to remove from video favorites');
      }
    }
  };

  const handleSaveToggle = (itemId, isSaved) => {
    if (!isSaved) {
      // Remove from list when unfavorited
      setFavorites(favorites.filter(f => f._id !== itemId));
      setUserSaves(userSaves.filter(id => id !== itemId));
    }
  };

  const handleVideoSaveToggle = (itemId, isSaved) => {
    if (!isSaved) {
      // Remove from list when unfavorited
      setVideoFavorites(videoFavorites.filter(v => v._id !== itemId));
      setUserVideoSaves(userVideoSaves.filter(id => id !== itemId));
    }
  };

  return (
    <>
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="inherit"
        TabIndicatorProps={{ style: { background: "var(--primary-color, #ffe066)" } }}
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            color: "var(--text-primary, #fff)",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            mr: 3,
            minWidth: 100,
          },
          "& .Mui-selected": {
            color: "var(--primary-color, #ffe066)",
          },
        }}
      >
        <Tab label="Music" />
        <Tab label="Videos" />
      </Tabs>

      {/* Music Tab */}
      {tab === 0 && (
        <>
          <Grid container spacing={2} sx={{
            color: "var(--text-primary, #fff)",
            mb: 1,
            px: 1,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 15,
            borderBottom: "1px solid var(--accent-color, #232323)",
            pb: 1.5,
          }}>
            <Grid item xs={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Title</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Listens</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Duration</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Added On</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" align="center" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>
                Actions
              </Typography>
            </Grid>
          </Grid>
      <Box>
        {loadingFavorites ? (
          <Typography color="var(--text-primary, #fff)">Loading your favorites...</Typography>
        ) : favorites.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="var(--text-primary, #fff)" sx={{ mb: 2 }}>No favorites yet.</Typography>
            <Typography color="var(--text-secondary, #999)">Songs you mark as favorite will appear here.</Typography>
          </Box>
        ) : (
          favorites.map((m, idx) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={m._id || idx}
              sx={{
                background: "var(--accent-color, #1a1a1a)",
                borderRadius: "12px",
                mb: 3,
                px: 1,
                paddingTop: 1,
                paddingBottom: 2.5,
                "&:hover": { background: "var(--background-color, #232323)" },
              }}
            >
              <Grid item xs={4}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ cursor: "pointer" }}
                  onClick={() => dispatch(addSongsAction(favorites, false, null, idx))}
                >
                  <Box
                    component="img"
                    src={m.thumbnailImg?.filePath || m.thumbnail || Images.circle_music}
                    alt={m.title}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      height: 48,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" color="#fff" fontWeight={700}>
                      {m.title}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="var(--primary-color, #ffea00)"
                      fontWeight={600}
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={e => {
                        e.stopPropagation();
                        window.location.href = `/app/artistprofile/${m.artistData?.[0]?.uId || ""}`;
                      }}
                    >
                      {m.artistData && m.artistData.length
                        ? m.artistData.map(a => a.displayArtistName).join(", ")
                        : m.artist || ""}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Typography color="var(--secondary-color, #ff8a3d)" fontWeight={500}>
                  {m.listenCount ?? m.listens ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography color="var(--secondary-color, #ff8a3d)" fontWeight={500}>
                  {HMS(m.duration)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography color="var(--text-secondary, #999)" fontWeight={500} fontSize={14}>
                  {m.savedAt ? new Date(m.savedAt).toLocaleDateString() : '-'}
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center", position: "relative" }}>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                  <FavoriteButton
                    itemId={m._id}
                    itemType="music"
                    userSaves={userSaves}
                    onSaveToggle={handleSaveToggle}
                    size={20}
                  />
                  <IconButton
                    aria-label="more"
                  onClick={() => {
                    setSelectedIndex(idx);
                    setOpen(idx);
                  }}
                  sx={{ color: "var(--text-primary, #fff)" }}
                >
                  <MoreVertIcon className="__more_icon" />
                </IconButton>
                </Stack>
                {selectedIndex === idx && open === idx && (
                  <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
                    <ProfileMenu
                      isLast={
                        idx === favorites.length - 1 && favorites.length > 2
                          ? "-90px !important"
                          : "40px"
                      }
                      right={"0px"}
                      isMenu={open === idx}
                      actions={getMenuActions(m)}
                      handleProfileMenuChoice={key => handleMenuAction(key, m._id, idx)}
                    />
                  </OutsideClickHandler>
                )}
              </Grid>
            </Grid>
          ))
        )}
      </Box>
        </>
      )}

      {/* Video Tab */}
      {tab === 1 && (
        <>
          <Grid container spacing={2} sx={{
            color: "var(--text-primary, #fff)",
            mb: 1,
            px: 1,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 15,
            borderBottom: "1px solid var(--accent-color, #232323)",
            pb: 1.5,
          }}>
            <Grid item xs={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Video</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Views</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Duration</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Added On</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" align="center" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>
                Actions
              </Typography>
            </Grid>
          </Grid>
          <Box>
            {loadingVideoFavorites ? (
              <Typography color="var(--text-primary, #fff)">Loading your video favorites...</Typography>
            ) : videoFavorites.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="var(--text-primary, #fff)" sx={{ mb: 2 }}>No video favorites yet.</Typography>
            <Typography color="var(--text-secondary, #999)">Videos you mark as favorite will appear here.</Typography>
              </Box>
            ) : (
              videoFavorites.map((v, idx) => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  key={v._id || idx}
                  sx={{
                    background: "var(--accent-color, #1a1a1a)",
                    borderRadius: "12px",
                    mb: 3,
                    px: 1,
                    paddingTop: 1,
                    paddingBottom: 2.5,
                    "&:hover": { background: "var(--background-color, #232323)" },
                  }}
                >
                  <Grid item xs={4}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        // Navigate to general videos route since we don't have playlist info from favorites
                        history.push(`/app/videos/${v._id}`);
                      }}
                    >
                      <Box
                        component="img"
                        src={v.thumbnail?.url || v.thumbnailUrl || '/placeholder-video.jpg'}
                        alt={v.title}
                        sx={{
                          width: 68,
                          height: 38,
                          borderRadius: "6px",
                          objectFit: "cover",
                          position: "relative",
                        }}
                      />
                      <PlayArrowIcon 
                        sx={{ 
                          position: "absolute", 
                          ml: 2.5, 
                          color: "rgba(255,255,255,0.8)", 
                          fontSize: 20 
                        }} 
                      />
                      <Box
                        sx={{
                          height: 48,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="body2" color="#fff" fontWeight={700}>
                          {v.title || 'Untitled Video'}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="var(--primary-color, #ffea00)"
                          fontWeight={600}
                          sx={{ textDecoration: "underline", cursor: "pointer" }}
                          onClick={e => {
                            e.stopPropagation();
                            if (v.uploader?._id) {
                              history.push(`/app/artistprofile/${v.uploader._id}`);
                            }
                          }}
                        >
                          {v.uploader?.displayArtistName || v.uploader?.fullName || 'Unknown Creator'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography color="var(--secondary-color, #ff8a3d)" fontWeight={500}>
                      {v.views || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography color="var(--secondary-color, #ff8a3d)" fontWeight={500}>
                      {HMS(v.duration || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography color="var(--text-secondary, #999)" fontWeight={500} fontSize={14}>
                      {v.savedAt ? new Date(v.savedAt).toLocaleDateString() : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "center", position: "relative" }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <FavoriteButton
                        itemId={v._id}
                        itemType="videos"
                        userSaves={userVideoSaves}
                        onSaveToggle={handleVideoSaveToggle}
                        size={20}
                      />
                      <IconButton
                        aria-label="more"
                      onClick={() => {
                        setSelectedIndex(idx);
                        setOpen(idx);
                      }}
                      sx={{ color: "var(--text-primary, #fff)" }}
                    >
                      <MoreVertIcon className="__more_icon" />
                    </IconButton>
                    </Stack>
                    {selectedIndex === idx && open === idx && (
                      <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
                        <ProfileMenu
                          isLast={
                            idx === videoFavorites.length - 1 && videoFavorites.length > 2
                              ? "-90px !important"
                              : "40px"
                          }
                          right={"0px"}
                          isMenu={open === idx}
                          actions={getVideoMenuActions(v)}
                          handleProfileMenuChoice={key => handleVideoMenuAction(key, v._id, idx)}
                        />
                      </OutsideClickHandler>
                    )}
                  </Grid>
                </Grid>
              ))
            )}
          </Box>
        </>
      )}

      {/* Studio/Playlist popup */}
      <AddMusicToPlaylist
        musicId={studioMusicId}
        isAlbum={false}
        open={showStudioPopup}
        handleClose={() => setShowStudioPopup(false)}
      />
    </>
  );
}
